import React, { FC, useState, useCallback, useEffect } from "react";
import { exportMarkdownToPDF, preloadPdfResources, preloadPdfResourcesForContent } from "./index";
import styles from "./index.module.scss";
import { toast } from "react-toastify";

interface MarkdownDownloadBtnProps {
  content: string;
  fileName?: string;
  buttonText?: string;
  className?: string;
  children?: React.JSX.Element | ((props: { loading: boolean }) => React.JSX.Element);
  loadingChildren?: React.JSX.Element | ((props: { loading: boolean }) => React.JSX.Element);
}

const MarkdownDownloadBtn: FC<MarkdownDownloadBtnProps> = ({
  content,
  fileName = "document.pdf",
  buttonText = "Export PDF",
  className,
  children,
  loadingChildren,
}) => {
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    preloadPdfResources();
  }, []);

  const handlePreload = useCallback(() => {
    preloadPdfResourcesForContent(content);
  }, [content]);

  const renderLoadingChildren = useCallback(() => {
    if (!loadingChildren) return null;
    return loadingChildren instanceof Function ? loadingChildren({ loading }) : loadingChildren;
  }, [loadingChildren, loading]);

  const handleDownload = useCallback(async () => {
    if (!content || !content.trim()) {
      toast.error("Content is empty, cannot generate PDF.");
      return;
    }

    setLoading(true);
    try {
      await exportMarkdownToPDF(content, fileName);
    } catch {
      toast.error("PDF generation failed, please try again later.");
    } finally {
      setLoading(false);
    }
  }, [content, fileName]);

  if (children) {
    return (
      <span
        className={className}
        onClick={loading ? undefined : handleDownload}
        onMouseEnter={handlePreload}
        onFocus={handlePreload}
        style={{
          cursor: loading ? "not-allowed" : "pointer",
          opacity: loading ? 0.6 : 1,
        }}
        title={loading ? "Generating PDF…" : undefined}
      >
        {loading && loadingChildren
          ? renderLoadingChildren()
          : children instanceof Function
            ? children({ loading })
            : children}
      </span>
    );
  }

  return (
    <button
      type="button"
      className={[styles.downloadBtn, className].filter(Boolean).join(" ")}
      onClick={handleDownload}
      onMouseEnter={handlePreload}
      onFocus={handlePreload}
      disabled={loading}
      title={loading ? "Generating PDF…" : buttonText}
    >
      {loading ? (
        loadingChildren ? (
          renderLoadingChildren()
        ) : (
          <>
            <span
              className={styles.spinner}
              aria-hidden="true"
            />
            <span>Generating…</span>
          </>
        )
      ) : (
        buttonText
      )}
    </button>
  );
};

export default MarkdownDownloadBtn;
